/**
 UseScopeEligibleRecordTypes

 Returns the published RecordTypes a user could create *for the current Library
 scope* — i.e. types carrying an OBJECT_REFERENCE field that points at one of the
 scope's object types. Creating such a record sets that reference to the scope
 record, which is what lands it under the scope's record folder.

 Eligibility is computed server-side via the `reference_object_type_id` filter:
 the list/query RecordType payload omits field definitions (`fields: []`), so we
 can't filter on fields client-side. We query once per distinct scope object
 * type and union the results (deduped by id), keeping only published types.
 *
 * The returned types carry id/name/prefix only (no fields) — enough for the
 * picker. The matching OBJECT_REFERENCE field key is resolved at create time by
 * fetching the full type detail.
 */

import {useMemo} from 'react';
import {useQueries} from '@tanstack/react-query';
import {getRecordTypesApi} from '@/matic/features/records/api/record-types-api';
import {recordTypeKeys} from '@/matic/features/records/query-keys';
import type {RecordType} from '@/matic/features/records/types';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';

type ScopeRef = {typeId: string};

export function useScopeEligibleRecordTypes(scopeItems: ScopeRef[]) {
  const tenantId = useRequiredTenantId();
  const api = getRecordTypesApi();

  const objectTypeIds = useMemo(
    () => [...new Set(scopeItems.map((item) => item.typeId))],
    [scopeItems]
  );

  const results = useQueries({
    queries: objectTypeIds.map((objectTypeId) => ({
      queryKey: recordTypeKeys.list(tenantId, {
        referenceObjectTypeId: objectTypeId,
      }),
      async queryFn() {
        return api.getRecordTypes({referenceObjectTypeId: objectTypeId});
      },
    })),
  });

  const isLoading = results.some((result) => result.isLoading);

  // Union the per-type results, deduped by id, published only. Depend on the
  // serialized id+publishedRevisionId pairs so the memo re-runs when a type is
  // published or unpublished without being added/removed from results.
  const idSignature = results
    .flatMap((result) =>
      (result.data?.items ?? []).map(
        (rt) => `${rt.id}:${rt.publishedRevisionId ?? ''}`
      )
    )
    .join(',');
  const eligibleTypes = useMemo(() => {
    const byId = new Map<string, RecordType>();
    for (const result of results) {
      for (const recordType of result.data?.items ?? []) {
        if (
          recordType.publishedRevisionId !== null &&
          recordType.publishedRevisionId !== undefined &&
          !byId.has(recordType.id)
        ) {
          byId.set(recordType.id, recordType);
        }
      }
    }

    return byId.values().toArray();
  }, [idSignature]);

  return {eligibleTypes, isLoading};
}
